import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Flame, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';
import { getColors } from '../theme';
import { useApp } from '../context/AppContext';
import ConsistencyRing from './ConsistencyRing';
import { 
  calculateGoalConsistency, 
  calculateRollingConsistency, 
  getConsistencyHistory 
} from '../utils/goalConsistencyUtils';

/**
 * LinkedHabitsModal - Detail modal showing linked habits and consistency trends
 * 
 * Features:
 * - Large aggregate consistency ring
 * - 7/14/30 day toggle for chart view
 * - Recharts AreaChart matching HabitsPage style
 * - List of linked habits with individual consistency and streak
 * - Edit Links button to open goal edit modal
 */
const LinkedHabitsModal = ({ goal, onClose, onEditLinks }) => {
  const { isDark, habits, completions } = useApp();
  const colors = getColors(isDark);
  const [chartDays, setChartDays] = useState(7);
  
  // Get linked habits data
  const linkedHabitIds = goal?.linkedHabitIds || [];
  const linkedHabits = useMemo(() => {
    return linkedHabitIds
      .map(id => habits.find(h => h.id === id))
      .filter(Boolean); // Remove any undefined (deleted habits)
  }, [linkedHabitIds, habits]);
  
  // Calculate aggregate consistency
  const aggregateConsistency = useMemo(() => {
    return calculateGoalConsistency(completions, linkedHabitIds, 7);
  }, [completions, linkedHabitIds]);
  
  // Generate chart data
  const chartData = useMemo(() => {
    return getConsistencyHistory(completions, linkedHabitIds, chartDays);
  }, [completions, linkedHabitIds, chartDays]);
  
  // Individual habit stats
  const habitStats = useMemo(() => {
    return linkedHabits.map(habit => ({
      ...habit,
      consistency: calculateRollingConsistency(completions, habit.id, 7),
      streak: habit.currentStreak || 0,
    }));
  }, [linkedHabits, completions]);
  
  if (!goal) return null;
  
  // Custom tooltip matching HabitsPage style
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div 
          className="px-3 py-2 rounded-lg "
          style={{ 
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.12)'
          }}
        >
          <p className="text-xs mb-1" style={{ color: colors.textMuted }}>
            {label}
          </p>
          <p className="text-sm font-semibold" style={{ color: colors.accent }}>
            {payload[0].value}% completed
          </p>
        </div>
      );
    }
    return null;
  };
  
  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 "
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-md max-h-[90vh] overflow-hidden rounded-lg animate-fade-in flex flex-col"
        style={{ 
          backgroundColor: colors.bg,
          border: `1px solid ${colors.border}`,
        }}
      >
        {/* Header */}
        <div className="p-6 pb-4 text-center border-b" style={{ borderColor: colors.border }}>
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full transition-transform"
            style={{ backgroundColor: colors.bgSecondary }}
          >
            <X size={18} style={{ color: colors.textMuted }} />
          </button>
          
          {/* Goal title */}
          <h2 
            className="text-lg font-semibold mb-4 pr-8"
            style={{ color: colors.textPrimary }}
          >
            {goal.title}
          </h2>
          
          {/* Large aggregate ring */}
          <div className="flex flex-col items-center gap-2">
            <ConsistencyRing 
              percentage={aggregateConsistency} 
              size={80} 
              strokeWidth={6}
            />
            <p className="text-xs" style={{ color: colors.textMuted }}>
              {linkedHabits.length} habit{linkedHabits.length !== 1 ? 's' : ''} linked
            </p>
          </div>
        </div>
        
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 pt-4">
          {/* Time range toggle */}
          <div className="flex justify-center mb-4">
            <div 
              className="flex rounded-lg overflow-hidden"
              style={{ 
                backgroundColor: colors.bgTertiary, 
                border: `1px solid ${colors.border}` 
              }}
            >
              {[7, 14, 30].map(days => (
                <button
                  key={days}
                  onClick={() => setChartDays(days)}
                  className="px-3 py-1.5 text-xs font-medium transition-all"
                  style={{
                    backgroundColor: chartDays === days ? colors.accent : 'transparent',
                    color: chartDays === days ? colors.onAccent : colors.textMuted
                  }}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>
          
          {/* Chart */}
          {linkedHabits.length > 0 && (
            <div 
              className="p-4 rounded-md mb-4"
              style={{ 
                backgroundColor: colors.bgSecondary,
                border: `1px solid ${colors.border}`
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={14} style={{ color: colors.accent }} />
                <span className="text-xs font-medium" style={{ color: colors.textSecondary }}>
                  Consistency Trend
                </span>
              </div>
              
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="linkedHabitsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.accent} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={colors.accent} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke={colors.border} 
                    strokeOpacity={0.3}
                    horizontal={true}
                    vertical={false}
                  />
                  <XAxis 
                    dataKey="label" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: colors.textMuted, fontSize: 10 }} 
                    dy={8}
                    interval={chartDays <= 7 ? 0 : chartDays <= 14 ? 1 : 'preserveStartEnd'}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: colors.textMuted, fontSize: 10 }} 
                    tickFormatter={(v) => `${v}%`}
                    width={35}
                    ticks={[0, 50, 100]}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="rate"
                    stroke={colors.accent}
                    strokeWidth={2.5}
                    fill="url(#linkedHabitsGradient)"
                    dot={{ fill: colors.accent, strokeWidth: 0, r: 3 }}
                    activeDot={{ r: 5, fill: colors.accent, stroke: colors.bg, strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
          
          {/* Linked habits list */}
          <div className="space-y-2">
            <h3 className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>
              LINKED HABITS
            </h3>
            
            {habitStats.length === 0 ? (
              <p className="text-sm text-center py-4" style={{ color: colors.textMuted }}>
                No habits linked to this goal
              </p>
            ) : (
              habitStats.map(habit => (
                <div 
                  key={habit.id}
                  className="flex items-center justify-between p-3 rounded-md"
                  style={{ 
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: colors.accent }}
                    />
                    <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>
                      {habit.name}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* Consistency percentage */}
                    <span 
                      className="text-sm font-semibold"
                      style={{ 
                        color: habit.consistency >= 67 ? colors.success : 
                               habit.consistency >= 34 ? colors.warning : colors.error 
                      }}
                    >
                      {habit.consistency}%
                    </span>
                    
                    {/* Streak */}
                    {habit.streak > 0 && (
                      <div className="flex items-center gap-1">
                        <Flame size={14} style={{ color: colors.accent }} />
                        <span className="text-xs font-medium" style={{ color: colors.textSecondary }}>
                          {habit.streak}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div 
          className="p-4 border-t"
          style={{ borderColor: colors.border }}
        >
          <button
            onClick={onEditLinks}
            className="w-full py-2.5 rounded-md font-medium transition-all "
            style={{ 
              backgroundColor: colors.bgSecondary,
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`
            }}
          >
            Edit Links
          </button>
        </div>
      </div>
    </div>
  );
  
  return ReactDOM.createPortal(modalContent, document.body);
};

export default LinkedHabitsModal;
